import { ExploreFilter } from "@/modules/explore/exploreTypes"
import { AddressDtoSchema } from "@modules/address/addressDto"
import { ChronoDtoSchema } from "@modules/chrono/services/chronoDtos"
import { PriceObjectDtoSchema } from "@modules/common/priceObject/gateway/priceObjectApiDto"
import { HUImageDtoSchema } from "@modules/dtos/imageDto"
import { UserDtoSchema } from "@modules/profile/userProfile/userDto"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { ProgramType, ProgramVisibility } from "@modules/programs/types/programTypes"
import { z } from "zod"

const ExploreBenchmarkItemDtoSchema = z.object({
  id: z.string(),
  type: z.literal(ExploreFilter.BENCHMARK),
  title: z.string(),
  category: z.string(),
  chrono: ChronoDtoSchema.nullable(),
  isFavorite: z.boolean(),
  rpe: z.number().nullable(),
})
const ExploreBoxItemDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  address: AddressDtoSchema,
  logo: HUImageDtoSchema,
  cover: HUImageDtoSchema,
  isSubscribed: z.boolean(),
  event: z.boolean().nullable(),
})
const ExploreProfileItemDtoSchema = z.object({
  id: z.string(),
  firstname: z.string(),
  lastname: z.string(),
  username: z.string(),
  avatar: HUImageDtoSchema.nullable(),
})
const ExploreProgramItemDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  image: HUImageDtoSchema.nullable(),
  owner: UserDtoSchema,
  visibility: z.enum(ProgramVisibility),
  subscribed: z.boolean(),
  subscriptionCount: z.number(),
  price: PriceObjectDtoSchema.nullable(),
  defaultHideSessionContent: z.boolean(),
  type: z.enum(ProgramType),
  exploreType: z.literal(ExploreFilter.PROGRAM),
})

const ExploreProProgramItemDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  image: HUImageDtoSchema.nullable(),
  owner: UserDtoSchema,
  visibility: z.enum(ProgramVisibility),
  subscribed: z.boolean(),
  subscriptionCount: z.number(),
  price: PriceObjectDtoSchema.nullable(),
  defaultHideSessionContent: z.boolean(),
  type: z.enum(ProgramType),
  exploreType: z.literal(ExploreFilter.PROGRAM_PRO),
})

// Reference to ProgramBundleItemDto in Swagger
export const ExploreProgramBundleItemDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    image: HUImageDtoSchema.nullable(),
    owner: UserDtoSchema,
    visibility: z.enum(ProgramVisibility),
    subscribed: z.boolean(),
    subscriptionCount: z.number(),
    price: PriceObjectDtoSchema.nullable(),
    type: z.enum(ProgramBundleType),
    nbPrograms: z.number(),
    exploreType: z.literal(ExploreFilter.PROGRAM_BUNDLE),
  })
  .meta({ id: "ExploreProgramBundleItemDtoSchema" })

export const ItemExploreContentTypesDtoSchema = z
  .union([
    ExploreBenchmarkItemDtoSchema,
    ExploreProgramItemDtoSchema,
    ExploreBoxItemDtoSchema,
    ExploreProfileItemDtoSchema,
  ])
  .meta({ id: "ItemExploreContentTypesDtoSchema" })

export const ExploreBaseContentDtoSchema = z
  .object({
    contentType: z.string(),
    content: ItemExploreContentTypesDtoSchema,
  })
  .meta({ id: "ExploreBaseContentDtoSchema" })

export const ExploreBenchmarkContentDtoSchema = ExploreBaseContentDtoSchema.extend({
  contentType: z.literal(ExploreFilter.BENCHMARK),
  content: ExploreBenchmarkItemDtoSchema,
}).meta({ id: "ExploreBenchmarkContentDtoSchema" })

export const ExploreProgamContentDtoSchema = ExploreBaseContentDtoSchema.extend({
  contentType: z.literal(ExploreFilter.PROGRAM),
  content: ExploreProgramItemDtoSchema,
}).meta({ id: "ExploreProgamContentDtoSchema" })

export const ExploreProgramBundleContentDtoSchema = ExploreBaseContentDtoSchema.extend({
  contentType: z.literal(ExploreFilter.PROGRAM_BUNDLE),
  content: ExploreProgramBundleItemDtoSchema,
}).meta({ id: "ExploreProgramBundleContentDtoSchema" })

export const ExploreProProgramContentDtoSchema = ExploreBaseContentDtoSchema.extend({
  contentType: z.literal(ExploreFilter.PROGRAM_PRO),
  content: ExploreProProgramItemDtoSchema,
}).meta({ id: "ExploreProProgramContentDtoSchema" })

export const ExploreBoxContentDtoSchema = ExploreBaseContentDtoSchema.extend({
  contentType: z.literal(ExploreFilter.BOX),
  content: ExploreBoxItemDtoSchema,
}).meta({ id: "ExploreBoxContentDtoSchema" })

export const ExploreProfileContentDtoSchema = ExploreBaseContentDtoSchema.extend({
  contentType: z.literal(ExploreFilter.PROFILE),
  content: ExploreProfileItemDtoSchema,
}).meta({ id: "ExploreProfileContentDtoSchema" })

export const ExploreContentDtoSchema = z
  .union([
    ExploreBenchmarkContentDtoSchema,
    ExploreProgamContentDtoSchema,
    ExploreProgramBundleContentDtoSchema,
    ExploreProProgramContentDtoSchema,
    ExploreBoxContentDtoSchema,
    ExploreProfileContentDtoSchema,
  ])
  .meta({ id: "ExploreContentDtoSchema" })

export type ItemExploreContentTypesDto = z.infer<typeof ItemExploreContentTypesDtoSchema>
export type ExploreBaseContentDto = z.infer<typeof ExploreBaseContentDtoSchema>
export type ExploreBenchmarkContentDto = z.infer<typeof ExploreBenchmarkContentDtoSchema>
export type ExploreProgamContentDto = z.infer<typeof ExploreProgamContentDtoSchema>
export type ExploreProProgramContentDto = z.infer<typeof ExploreProProgramContentDtoSchema>
export type ExploreProgramBundleDto = z.infer<typeof ExploreProgramBundleContentDtoSchema>
export type ExploreBoxContentDto = z.infer<typeof ExploreBoxContentDtoSchema>
export type ExploreProfileContentDto = z.infer<typeof ExploreProfileContentDtoSchema>
export type ExploreContentDto = z.infer<typeof ExploreContentDtoSchema>

export type ExploreBenchmarkItemDto = z.infer<typeof ExploreBenchmarkItemDtoSchema>
export type ExploreBoxItemDto = z.infer<typeof ExploreBoxItemDtoSchema>
export type ExploreProfileItemDto = z.infer<typeof ExploreProfileItemDtoSchema>
export type ExploreProgramItemDto = z.infer<typeof ExploreProgramItemDtoSchema>
export type ExploreProgramBundleItemDto = z.infer<typeof ExploreProgramBundleItemDtoSchema>
export type ExploreProProgramItemDto = z.infer<typeof ExploreProProgramItemDtoSchema>
